import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Import all client images following project pattern
import client1 from "../../assets/clients/1.jpeg";
import client2 from "../../assets/clients/2.jpeg";
import client3 from "../../assets/clients/3.jpeg";
import client4 from "../../assets/clients/4.jpeg";
import client5 from "../../assets/clients/5.jpeg";
import client6 from "../../assets/clients/6.jpeg";
import client7 from "../../assets/clients/7.png";
import client8 from "../../assets/clients/8.png";
import client9 from "../../assets/clients/9.png";
import client10 from "../../assets/clients/10.png";
import client11 from "../../assets/clients/11.jpeg";
import client12 from "../../assets/clients/12.jpeg";
import client13 from "../../assets/clients/13.jpeg";

const clients = [
  { id: 1, imageUrl: client1, alt: "Client logo 1" },
  { id: 2, imageUrl: client2, alt: "Client logo 2" },
  { id: 3, imageUrl: client3, alt: "Client logo 3" },
  { id: 4, imageUrl: client4, alt: "Client logo 4" },
  { id: 5, imageUrl: client5, alt: "Client logo 5" },
  { id: 6, imageUrl: client6, alt: "Client logo 6" },
  { id: 7, imageUrl: client7, alt: "Client logo 7" },
  { id: 8, imageUrl: client8, alt: "Client logo 8" },
  { id: 9, imageUrl: client9, alt: "Client logo 9" },
  { id: 10, imageUrl: client10, alt: "Client logo 10" },
  { id: 11, imageUrl: client11, alt: "Client logo 11" },
  { id: 12, imageUrl: client12, alt: "Client logo 12" },
  { id: 13, imageUrl: client13, alt: "Client logo 13" },
];

const AUTO_MS = 3000;
const DUPE_FACTOR = 4;

function OurClients() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(2);
  const [paused, setPaused] = useState(false);
  const sliderRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const extendedClients = Array.from({ length: DUPE_FACTOR }, (_, i) =>
    clients.map((client) => ({
      ...client,
      displayId: client.id + i * clients.length,
    })),
  ).flat();

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % clients.length);
  }, []);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + clients.length) % clients.length);
  }, []);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;

    if (distance > 50) {
      goNext(); // swipe left
    } else if (distance < -50) {
      goPrev(); // swipe right
    }
  };

  // Responsive visible count
  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth >= 1024) {
        setVisibleCount(6);
      } else if (window.innerWidth >= 768) {
        setVisibleCount(4);
      } else {
        setVisibleCount(2);
      }
    };

    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);
    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);

  useEffect(() => {
    if (paused) return;
    const interval = setInterval(goNext, AUTO_MS);
    return () => clearInterval(interval);
  }, [paused, goNext]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    const translateX = -currentIndex * (100 / visibleCount);
    slider.style.transform = `translateX(${translateX}%)`;
  }, [currentIndex, visibleCount]);

  return (
    <section
      className="relative py-16 md:py-20 lg:py-24 bg-surface border-t border-white/[0.06] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="container relative z-10 mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
            Our <span className="text-gradient-brand">Clients</span>
          </h2>
        </div>

        <div className="relative group/clients">
          {/* Desktop Marquee - No cards, no progress bar */}
          <div className="">
            <div className="relative overflow-hidden"
             onTouchStart={handleTouchStart}
             onTouchMove={handleTouchMove}
             onTouchEnd={handleTouchEnd}
            >
              <div
                ref={sliderRef}
                className="flex items-center whitespace-nowrap transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
              >
                {extendedClients.map((client) => (
                  <div
                    key={client.displayId}
                    className="flex-none w-1/2 md:w-1/4 lg:w-1/6 h-20 md:h-28 lg:h-36 p-2 md:p-4 flex items-center justify-center transition-all duration-300 hover:scale-105 filter brightness-90 hover:brightness-100"
                  >
                    <img
                      src={client.imageUrl}
                      alt={client.alt}
                      className="h-16 md:h-20 lg:h-24 xl:h-28 w-auto h-auto max-h-full object-contain mx-auto transition-all duration-500"
                      draggable={false}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Arrows */}
            <button
              onClick={goPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 hidden md:flex h-12 w-12 items-center justify-center rounded-full bg-black/50 hover:bg-brand-orange text-white backdrop-blur-sm transition-all duration-300 hover:scale-110"
              aria-label="Previous client logo"
              type="button"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 hidden md:flex h-12 w-12 items-center justify-center rounded-full bg-black/50 hover:bg-brand-orange text-white backdrop-blur-sm transition-all duration-300 hover:scale-110"
              aria-label="Next client logo"
              type="button"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default OurClients;
